<script>
import RawData from "@/components/widgets/raw-data.vue";
export default {
    props: {
        modalTitle: {
            type: String,
        },
        viewing: {
            type: Boolean,
            default: false
        }
    },
    components: {
        RawData
    },
    computed: {
        filtered() {
            if (!this.search) {
                return this.items;
            }
            return this.items.filter(item => item.filterKey.toLowerCase().includes(this.search.toLowerCase()));
        },
        rows() {
            return this.filtered.length;
        },
        fields() {
            return [
                { key: "propertyName", label: "Property Name", sortable: true },
                { key: "location", label: "Location", sortable: true },
                { key: "assignment", label: "Assignment Details", sortable: true },
            ];
        },
        hasUpdated() {
            return JSON.stringify(this.items) != this.oldItems;
        }
    },
    data() {
        return {
            items: [],
            loading: false,
            perPage: 10,
            currentPage: 1,
            sortBy: "id",
            sortDesc: true,
            search: "",
            oldItems: [],
            statusList: ['Missing/Unfound', 'Unserviceable/Beyond Repair', 'Unserviceable/Repairable', 'Others']
        }
    },
    methods: {
        setItems(items) {
            this.items = JSON.parse(JSON.stringify(items));
            this.oldItems = JSON.stringify(items);
        },
        onSubmit(submitApprove){
            this.$emit("update-items", {items: this.items, submitApprove});
        }
    },
}
</script>
<template>
    <b-modal
        id="return-items"
        :title="modalTitle"
        size="xl"
        title-class="font-18"
        hide-footer
        no-close-on-backdrop
    >
        <div class="mb-2 d-flex gap-1 align-items-center">
            <div class="w-300px">
                <input type="text" class="form-control" placeholder="Search Property..." v-model="search">
            </div>
            <select v-model="perPage" class="form-select w-auto">
                <option>5</option>
                <option>10</option>
                <option>25</option>
                <option>50</option>
                <option>100</option>
            </select>
        </div>
        <b-table
            class="datatables target-table"
            :items="filtered"
            :fields="fields"
            responsive="sm"
            :per-page="perPage"
            :current-page="currentPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :busy="loading"
            bordered
            outlined
            striped
            show-empty
            >
            <template #empty>
                <div class="text-center">
                    <div v-if="search != ''">
                        <i class="fas fa-exclamation-triangle me-2"></i>There are no properties matching your request
                    </div>
                    <div v-else>
                        <i class="fas fa-exclamation-triangle me-2"></i>No properties available
                    </div>
                </div>
            </template>
            <template #cell(propertyName)="row">
                <div class="position-relative">
                    <RawData :data="row.item" v-if="$store.state.decrypt.isAdmin()" />
                    {{ row.item.propertyName ? row.item.propertyName : "N/A" }}
                    <div class="w-300px cursor-pointer hover-underline mb-2" @click="row.item.clamped = !row.item.clamped">
                        <small :class="{'clamp-text': !row.item.clamped}">{{ row.item.propertySpecification }}</small>
                    </div>
                    <template v-if="!viewing">
                        <select class="form-select text-warning" v-model="row.item.statusReturn" >
                            <option v-for="(status, index) in statusList" :key="index" :value="status">{{ status }}</option>
                        </select>
                        <div class="mt-2 ms-4" v-if="row.item.otherReason">
                            <label for="otherReason" class="mb-0 text-center">Other Reason</label>
                            <input type="text" class="form-control mt-1" id="otherReason" placeholder="Other Reason" v-model="row.item.otherReason" v-if="row.item.statusReturn === 'Others'">
                        </div>
                    </template>
                    <span v-else>Property Status: <span class="text-warning">{{ row.item.statusReturn || "N/A" }}{{ row.item.otherReason ? ` - ${row.item.otherReason}` : "" }}</span></span>
                </div>
            </template>
            <template #cell(location)="row">
                {{ row.item.propertyLocation ? row.item.propertyLocation.location : "N/A" }}
            </template>
            <template #cell(dateReturned)="row">
                {{ row.item.dateReturned ? formatDate(new Date(row.item.dateReturned)) : "N/A" }}
            </template>
            <template #cell(assignment)="row">
                <div class="d-flex gap-1 align-items-end">
                    <span class="font-size-11">Date Assigned:</span><span class="text-white"> {{ formatDate(new Date(row.item.dateAssigned)) }}</span>
                </div>
                <div class="d-flex gap-1 align-items-end">
                    <span class="font-size-11">Number:</span><span class="text-white"> {{ row.item.assignmentNumber }}</span>
                </div>
                <div class="d-flex gap-1 align-items-end" v-if="row.item.assignNotes">
                    <span class="font-size-11">Note:</span><span class="text-white"> {{ row.item.assignNotes }}</span>
                </div>
                <div>
                    <span class="bg-success badge">{{ row.item.assignmentType }}</span>
                </div>
            </template>
        </b-table>
        <div class="d-flex justify-content-end">
            <div class="mb-3">
                <ul class="pagination pagination-rounded mb-0" style="z-index: -1">
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="rows"
                        :per-page="perPage"
                    ></b-pagination>
                </ul>
            </div>
        </div>
        <div class="modal-footer">
            <button 
                type="button" 
                class="btn btn-success" 
                v-if="hasUpdated"
                @click="onSubmit(true)"
            >
                <i class="bx bx-check me-1"></i>
                Update & Approve
            </button>
            <button 
                type="button" 
                class="btn btn-primary" 
                v-if="hasUpdated"
                @click="onSubmit(false)"
            >
                <i class="bx bx-save me-1"></i>
                Update Only
            </button>
            <button 
                type="button" 
                class="btn btn-link" 
                @click="$bvModal.hide('return-items')"
            >
                Cancel
            </button>
        </div>
    </b-modal>
</template>